import React, { useEffect, useRef } from "react";

import { AdvertImageNotAvailable, Usage } from "@at/design-system-patterns";
import { GalleryImage102 } from "@at/track-core";

import {
  GalleryGridMainImage,
  GalleryGridThumbnailImage,
} from "./gallery-grid-images";
import {
  StyledGalleryGridContainer,
  StyledGalleryGridImage,
} from "./gallery-grid.styled";
import {
  getGalleryImageContext,
  ImageGalleryState,
} from "../../../utils/tracking/tracking-data-utils";
import { useTracking } from "../../../utils/tracking/use-tracking";
import {
  GalleryImage,
  ImageOnClickHandler,
} from "../gallery-image/gallery-image";
import { GalleryStats } from "../gallery-stats/gallery-stats";
import { GalleryThumbnailsLength } from "../gallery.types";
import { GalleryAsset } from "../gallery.utils";
import { FpaGridViewMoreImages } from "../view-more-images/view-more-images";

interface GalleryGridProps {
  images: GalleryAsset[];
  isLeasingPDPPage?: boolean;
  hasSpin: boolean;
  hasVideo: boolean;
  showViewMore: boolean;
  onImageClick: ImageOnClickHandler;
}

export function GalleryGrid({
  images,
  isLeasingPDPPage,
  hasSpin,
  hasVideo,
  showViewMore,
  onImageClick,
}: GalleryGridProps) {
  const { trackComponentViewedEvent } = useTracking();
  const trackedGridImages = useRef<GalleryImage102[]>([]);

  const gridThumbnailsLength = isLeasingPDPPage
    ? GalleryThumbnailsLength.SMALL
    : GalleryThumbnailsLength.MEDIUM;

  const gridImages: (GalleryAsset | undefined)[] = Array.from({
    length: gridThumbnailsLength,
  });

  for (const [index, asset] of images
    .slice(0, gridThumbnailsLength)
    .entries()) {
    gridImages[index] = asset;
  }

  useEffect(() => {
    trackedGridImages.current = gridImages
      .filter((asset): asset is GalleryAsset => !!asset)
      .map((asset, index) => {
        const imageId = asset.image.url.match(/([\dA-Za-z]+)\.jpg/)?.[1] ?? "";
        return getGalleryImageContext(
          imageId,
          ImageGalleryState.in_page,
          index,
        );
      });

    trackComponentViewedEvent({ extraEntities: trackedGridImages.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledGalleryGridContainer
      isLeasingPage={isLeasingPDPPage}
      data-testid={"gallery-grid-image-container"}
    >
      {gridImages.map((asset, index) => {
        const src = asset?.image.url;
        const alt = asset?.image.alt;
        const isMainImage = index === 0;
        return (
          <StyledGalleryGridImage
            key={asset ? `${asset?.type}-${asset?.image?.url}` : index}
          >
            {src ? (
              <GalleryImage
                {...{
                  src,
                  index,
                  dataTestId: `open-carousel-${index + 1}`,
                  onClick: onImageClick,
                }}
              >
                {isMainImage ? (
                  <GalleryGridMainImage src={src} alt={alt} />
                ) : (
                  <GalleryGridThumbnailImage src={src} alt={alt} />
                )}
              </GalleryImage>
            ) : (
              <AdvertImageNotAvailable usage={Usage.Main} />
            )}

            {showViewMore && index === 0 && (
              <FpaGridViewMoreImages
                showSlide={() => {
                  onImageClick({ index });
                }}
                viewSpin={!!hasSpin}
                viewVideo={!!hasVideo}
                viewImage={images.length > 0}
              />
            )}
          </StyledGalleryGridImage>
        );
      })}

      {(images.length > 0 || !!hasSpin || !!hasVideo) && (
        <GalleryStats
          {...{
            imageCount: images.length,
            hasSpinUrl: !!hasSpin,
            hasVideoUrl: !!hasVideo,
          }}
        />
      )}
    </StyledGalleryGridContainer>
  );
}
